import dayjs from '../../../utils/dayjsConfig'
import { formatRbacAuditSummary } from '../../utils/permissions'
import { scopeStateKey, scopeTitle } from '../../roles/RoleWizard/roleWizardConstants'
import { RBAC_USER_DETAIL_TEXTS } from '../../pages/shared/rbacUserDetailConstants'
import {
  ROLE_ACTION_CHIPS,
  ROLE_PROFILE_FIELD_LABELS,
  USER_ACTION_CHIPS,
  USER_PROFILE_FIELD_LABELS
} from './rbacAuditHistoryConstants'

const AUDIT_ACTION_LABELS = {
  READ_WRITE: 'todo editar',
  READ: 'solo ver',
  DOWNLOADS: 'descargar',
  DOWNLOAD: 'descargar',
  OFF: 'apagado'
}

export function splitAuditDateTime(value) {
  if (!value) {
    return { date: '—', time: '' }
  }

  const parsed = dayjs(value)
  return {
    date: parsed.format('DD-MM-YYYY'),
    time: parsed.format('HH:mm:ss')
  }
}

export function isSystemActor(entry) {
  if (!entry?.actor_name && !entry?.actor_email) return true
  if (entry.actor_source && entry.actor_source !== 'admin-ui') return true
  return false
}

export function getActionChipConfig(context, action) {
  const map = context === 'role' ? ROLE_ACTION_CHIPS : USER_ACTION_CHIPS
  return (
    map[action] || {
      label: action || '—',
      variant: 'blue'
    }
  )
}

export function buildPermissionLabelMap(catalog) {
  return Object.fromEntries(
    (catalog?.modules || []).flatMap((module) =>
      (module.scopes || []).map((scope) => [
        scopeStateKey(module.key, scope.key),
        scopeTitle(module.key, scope)
      ])
    )
  )
}

function indexRoles(roles) {
  return Object.fromEntries(
    (roles || []).map((role) => {
      const roleId = role?.id ?? role?.role_id
      return [roleId, role]
    })
  )
}

function normalizePermissionActions(actions) {
  return [...(actions || [])].map(String).sort()
}

function permissionKey(moduleKey, scopeKey) {
  return scopeStateKey(moduleKey, scopeKey)
}

function indexPermissions(permissions) {
  return Object.fromEntries(
    (permissions || []).map((permission) => {
      const moduleKey = permission.module_key || permission.moduleKey
      const scopeKey = permission.scope_key || permission.scopeKey
      const key = permissionKey(moduleKey, scopeKey)
      return [
        key,
        {
          moduleKey,
          scopeKey,
          actions: normalizePermissionActions(permission.actions)
        }
      ]
    })
  )
}

export function formatPermissionActions(actions) {
  const values = normalizePermissionActions(actions)
  if (!values.length) return 'apagado'
  return values.map((action) => AUDIT_ACTION_LABELS[action] || action).join(', ')
}

function permissionLabel(permission, labelMap) {
  const key = permissionKey(permission.moduleKey, permission.scopeKey)
  return labelMap[key] || permission.scopeKey
}

export function buildProfileChanges(before, after, fieldLabels) {
  return Object.entries(fieldLabels)
    .map(([field, label]) => {
      const beforeValue = before?.[field]
      const afterValue = after?.[field]
      if (beforeValue === afterValue) return null
      if (beforeValue == null && afterValue == null) return null

      return {
        fieldLabel: label,
        before: beforeValue ?? '',
        after: afterValue ?? '',
        kind: 'change'
      }
    })
    .filter(Boolean)
}

export function buildRoleAssignmentChanges(before, after) {
  const beforeRoles = indexRoles(before?.roles)
  const afterRoles = indexRoles(after?.roles)
  const beforeIds = Object.keys(beforeRoles)
  const afterIds = Object.keys(afterRoles)
  const addedIds = afterIds.filter((id) => !beforeIds.includes(id))
  const removedIds = beforeIds.filter((id) => !afterIds.includes(id))

  if (removedIds.length === 1 && addedIds.length === 1) {
    return [
      {
        fieldLabel: 'Rol',
        before: beforeRoles[removedIds[0]]?.name || '',
        after: afterRoles[addedIds[0]]?.name || '',
        kind: 'change'
      }
    ]
  }

  return [
    ...addedIds.map((id) => ({
      fieldLabel: 'Rol asignado',
      before: '',
      after: afterRoles[id]?.name || '',
      kind: 'added'
    })),
    ...removedIds.map((id) => ({
      fieldLabel: 'Rol quitado',
      before: beforeRoles[id]?.name || '',
      after: '',
      kind: 'removed'
    }))
  ]
}

export function buildPermissionChanges(before, after, labelMap = {}) {
  const beforeMap = indexPermissions(before?.permissions)
  const afterMap = indexPermissions(after?.permissions)
  const beforeKeys = Object.keys(beforeMap)
  const afterKeys = Object.keys(afterMap)
  const addedKeys = afterKeys.filter((key) => !beforeKeys.includes(key))
  const removedKeys = beforeKeys.filter((key) => !afterKeys.includes(key))
  const modifiedKeys = beforeKeys.filter((key) => {
    if (!afterKeys.includes(key)) return false
    return beforeMap[key].actions.join(',') !== afterMap[key].actions.join(',')
  })

  return [
    ...addedKeys.map((key) => ({
      changeType: 'permission',
      fieldLabel: permissionLabel(afterMap[key], labelMap),
      beforeActions: [],
      afterActions: afterMap[key].actions,
      kind: 'added'
    })),
    ...removedKeys.map((key) => ({
      changeType: 'permission',
      fieldLabel: permissionLabel(beforeMap[key], labelMap),
      beforeActions: beforeMap[key].actions,
      afterActions: [],
      kind: 'removed'
    })),
    ...modifiedKeys.map((key) => ({
      changeType: 'permission',
      fieldLabel: permissionLabel(beforeMap[key], labelMap),
      beforeActions: beforeMap[key].actions,
      afterActions: afterMap[key].actions,
      kind: 'change'
    }))
  ]
}

function assignmentItems(selection = {}) {
  const items = new Map((selection.items || []).map((item) => [String(item.uid ?? item.id), item]))
  return [...new Set((selection.uids || selection.ids || []).map(String))].map((uid) => ({
    uid,
    name: items.get(uid)?.name || ''
  }))
}

function assignmentLabel(selection, items) {
  if (selection.mode === 'all') return RBAC_USER_DETAIL_TEXTS.appUsersAll
  if (!items.length) return RBAC_USER_DETAIL_TEXTS.appUsersNone
  return `${items.length} ${items.length === 1 ? 'usuario asignado' : 'usuarios asignados'}`
}

export function buildAppUserAssignmentDetail(before = {}, after = {}) {
  const beforeSelection = before.app_users || { mode: 'none' }
  const afterSelection = after.app_users || { mode: 'none' }
  const beforeItems = assignmentItems(beforeSelection)
  const afterItems = assignmentItems(afterSelection)
  const beforeUids = new Set(beforeItems.map((item) => item.uid))
  const afterUids = new Set(afterItems.map((item) => item.uid))
  const added = afterItems.filter((item) => !beforeUids.has(item.uid))
  const removed = beforeItems.filter((item) => !afterUids.has(item.uid))
  const allTransition = beforeSelection.mode === 'all' || afterSelection.mode === 'all'
  const groups = allTransition
    ? [
        {
          label: RBAC_USER_DETAIL_TEXTS.appUsersPreviousSelection,
          items: beforeItems,
          kind: 'previous'
        },
        {
          label: RBAC_USER_DETAIL_TEXTS.appUsersCurrentSelection,
          items: afterItems,
          kind: 'current'
        }
      ]
    : [
        { label: RBAC_USER_DETAIL_TEXTS.appUsersAdded, items: added, kind: 'added' },
        { label: RBAC_USER_DETAIL_TEXTS.appUsersRemoved, items: removed, kind: 'removed' }
      ]
  const counts = [
    added.length &&
      `${added.length} ${added.length === 1 ? 'usuario agregado' : 'usuarios agregados'}`,
    removed.length &&
      `${removed.length} ${removed.length === 1 ? 'usuario retirado' : 'usuarios retirados'}`
  ].filter(Boolean)
  return {
    type: 'app-user-assignments',
    summary: allTransition
      ? `${assignmentLabel(beforeSelection, beforeItems)} → ${assignmentLabel(afterSelection, afterItems)}`
      : counts.join(' · ') || 'Sin cambios en los usuarios asignados.',
    currentAccess: assignmentLabel(afterSelection, afterItems),
    showCurrentAccess: !allTransition,
    groups: groups.filter((group) => group.items.length > 0)
  }
}

export function buildAuditDetail(entry, context, labelMap = {}) {
  const before = entry?.changes_before || {}
  const after = entry?.changes_after || {}
  const action = entry?.action

  if (context === 'user') {
    if (['create_app_user_relation', 'update_app_user_relation'].includes(action)) {
      if (before.app_users || after.app_users) return buildAppUserAssignmentDetail(before, after)
    }
    if (action === 'assign_roles') {
      const changes = buildRoleAssignmentChanges(before, after)
      if (changes.length > 0) {
        return { type: 'changes', changes }
      }
    }

    if (action === 'update') {
      const changes = buildProfileChanges(before, after, USER_PROFILE_FIELD_LABELS)
      if (changes.length > 0) {
        return { type: 'changes', changes }
      }
    }

    if (action === 'account_activated') {
      return {
        type: 'system',
        summary: formatRbacAuditSummary(entry.summary)
      }
    }
  }

  if (context === 'role') {
    if (action === 'update_permissions') {
      const changes = buildPermissionChanges(before, after, labelMap)
      if (changes.length >= 2) {
        return {
          type: 'accordion',
          summary: `Se modificaron ${changes.length} permisos en el rol.`,
          changes
        }
      }
      if (changes.length === 1) {
        return { type: 'changes', changes }
      }
    }

    if (action === 'update') {
      const changes = buildProfileChanges(before, after, ROLE_PROFILE_FIELD_LABELS)
      if (changes.length === 1 && changes[0].fieldLabel === 'Descripción') {
        return { type: 'description', change: changes[0] }
      }
      if (changes.length > 0) {
        return { type: 'changes', changes }
      }
    }
  }

  return {
    type: 'muted',
    summary: formatRbacAuditSummary(entry?.summary)
  }
}
